import { FC, useEffect } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import {
  useGetCompanyListBasicQuery,
  SortDir,
  type OpportunityGeneratorFilter,
  type GetCompanyListBasicQueryVariables,
} from "@/generated/company-search";
import { COMPANY_SEARCH_CLIENT } from "@/utils/consts";
import {
  bandRange,
  deltaBackground,
  deltaTextColor,
  type Band,
  type MatrixThresholds,
} from "@/lib/scoring/matrix/bands";

type ScoringProfilesInput =
  GetCompanyListBasicQueryVariables["scoringProfiles"];

interface CompareCellContainerProps {
  baseFilters?: OpportunityGeneratorFilter;
  scoringProfilesA: ScoringProfilesInput;
  scoringProfilesB: ScoringProfilesInput;
  localizeInstalls: boolean;
  fit: Band;
  intent: Band;
  thresholds: MatrixThresholds;
  onLoadingChange?: (loading: boolean) => void;
}

function formatDelta(delta: number): string {
  return delta > 0 ? `+${delta}` : `${delta}`;
}

/** One Fit×Intent cell showing the A−B count delta (count-only queries). */
export const CompareCellContainer: FC<CompareCellContainerProps> = ({
  baseFilters,
  scoringProfilesA,
  scoringProfilesB,
  localizeInstalls,
  fit,
  intent,
  thresholds,
  onLoadingChange,
}) => {
  const variables = {
    filters: {
      ...(baseFilters ?? {}),
      fitScore: bandRange(fit, thresholds),
      intentScore: bandRange(intent, thresholds),
    },
    localizeInstalls,
    page: 0,
    per: 1,
    sort: [{ field: "revenue_range_min", direction: SortDir.DESC }],
  };

  const a = useGetCompanyListBasicQuery({
    variables: { ...variables, scoringProfiles: scoringProfilesA },
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });
  const b = useGetCompanyListBasicQuery({
    variables: { ...variables, scoringProfiles: scoringProfilesB },
    context: { clientName: COMPANY_SEARCH_CLIENT },
  });

  const loading = (a.loading && !a.data) || (b.loading && !b.data);

  useEffect(() => {
    onLoadingChange?.(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const delta =
    (a.data?.opportunity_generator_search?.count ?? 0) -
    (b.data?.opportunity_generator_search?.count ?? 0);

  return (
    <Box
      data-testid={`compare-cell-${fit}-${intent}`}
      sx={{
        bgcolor: loading ? "grey.100" : deltaBackground(delta),
        borderRadius: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {loading ? (
        <Skeleton variant="text" width={48} height={40} />
      ) : (
        <Typography
          fontWeight={700}
          sx={{
            color: deltaTextColor(delta),
            fontSize: { xs: "1.1rem", sm: "1.5rem", md: "2.125rem" },
          }}
        >
          {formatDelta(delta)}
        </Typography>
      )}
    </Box>
  );
};
